// Unified census file parser — accepts CSV / XLSX / XLS, returns normalized
// sheet-by-sheet structure with header-row detection. Handles real-world files
// (e.g., TriMed.xlsx) where headers appear on row 5 with title rows above.
//
// Output:
//   {
//     sheets: [
//       {
//         name: string,
//         rows: any[][],            // raw 2D data
//         headerRowIndex: number,   // 0-based row index of detected headers
//         headers: string[],        // trimmed header strings (filtered for empty)
//         headerMap: { [normalizedHeader: string]: number },  // header -> col index
//         dataRows: object[],       // row objects keyed by header (excludes totals row)
//         totalsRow: object|null,   // detected totals row at the bottom (or null)
//         meta: { totalRows, dataRowCount }
//       },
//       ...
//     ],
//     fileName: string,
//     fileType: 'csv' | 'xlsx' | 'xls'
//   }

import * as XLSX from 'xlsx';

const HEADER_KEYWORDS = [
  'name', 'first', 'last', 'employee', 'salary', 'wages', 'gross', 'pay',
  'state', 'filing', 'marital', 'email', 'department', 'hire', 'hours',
  'reg', 'ot', 'overtime', 'futaer', 'medier', 'ssecer', 'total', 'dob',
  'ssn', 'pay schedule', 'yearly', 'annual', 'frequency', 'worker',
];

function scoreHeaderRow(row) {
  if (!Array.isArray(row) || row.length === 0) return 0;
  const cells = row.map((c) => (c === null || c === undefined ? '' : String(c)).trim());
  const nonEmpty = cells.filter((c) => c.length > 0);
  if (nonEmpty.length < 3) return 0; // too sparse — likely a title/section row
  const stringCells = nonEmpty.filter((c) => isNaN(Number(c.replace(/[$,]/g, ''))));
  const stringRatio = stringCells.length / nonEmpty.length;
  if (stringRatio < 0.7) return 0; // likely a data row (mostly numbers)
  const lowered = nonEmpty.map((c) => c.toLowerCase());
  let matches = 0;
  for (const kw of HEADER_KEYWORDS) {
    if (lowered.some((c) => c.includes(kw))) matches += 1;
  }
  if (matches < 2) return 0; // not header-like at all
  // Score = keyword matches × non-empty cell count × string-ratio.
  // The real column-header row has BOTH many keyword matches AND many cells
  // (e.g., 23 columns), while section sub-headers like ["Earnings", "ER Taxes",
  // "Total Payroll"] have few cells and lose against a 23-column header row.
  return matches * nonEmpty.length * stringRatio;
}

function detectHeaderRowIndex(rows) {
  const limit = Math.min(15, rows.length);
  let best = { idx: 0, score: 0 };
  for (let i = 0; i < limit; i++) {
    const s = scoreHeaderRow(rows[i]);
    if (s > best.score) best = { idx: i, score: s };
  }
  // If no row scored, fall back to row 0
  return best.score > 0 ? best.idx : 0;
}

function normalizeHeader(h) {
  if (h === null || h === undefined) return '';
  return String(h).replace(/\*/g, '').replace(/\s+/g, ' ').trim();
}

// Detect a "totals" row near the bottom that has aggregate sums but no employee
// identifiers. ADP exports include a final summed row with empty Employee ID.
function detectTotalsRow(dataRows, headerMap) {
  if (!dataRows.length) return { dataRows, totalsRow: null };
  const last = dataRows[dataRows.length - 1];
  // Heuristic: if employee identifier columns are blank but sums appear in
  // dedicated employer-tax columns, treat as totals row. We deliberately do
  // NOT match on the generic "total" keyword here -- a payroll export that
  // happens to have a "Total" column but no ER-tax columns shouldn't trigger
  // false-positive totals detection on its last row.
  const idCols = Object.keys(headerMap).filter((k) =>
    /(employee|first.?name|last.?name|^name$)/i.test(k)
  );
  const taxCols = Object.keys(headerMap).filter((k) =>
    /(ssecer|medier|futaer|nymta|nysuier)/i.test(k)
  );
  if (taxCols.length === 0) return { dataRows, totalsRow: null };
  const idValues = idCols.map((k) => last[k]).filter((v) => v !== null && v !== undefined && String(v).trim() !== '');
  const taxValues = taxCols.map((k) => last[k]).filter((v) => v !== null && v !== undefined && Number(v) > 0);
  if (idValues.length === 0 && taxValues.length >= 2) {
    return {
      dataRows: dataRows.slice(0, -1),
      totalsRow: last,
    };
  }
  return { dataRows, totalsRow: null };
}

function buildSheetView(name, rawRows) {
  // Strip fully-empty trailing rows
  const rows = rawRows.filter((r) => Array.isArray(r) && r.some((c) => c !== null && c !== undefined && String(c).trim() !== ''));
  if (rows.length === 0) {
    return {
      name,
      rows: [],
      headerRowIndex: 0,
      headers: [],
      headerMap: {},
      dataRows: [],
      totalsRow: null,
      meta: { totalRows: 0, dataRowCount: 0 },
    };
  }
  const headerRowIndex = detectHeaderRowIndex(rows);
  const headerRow = rows[headerRowIndex] || [];
  // Disambiguate duplicate header names (Excel allows two columns called
  // "Total"). First occurrence keeps the bare name; subsequent duplicates get
  // "_2", "_3", ... suffixes. Both are kept so transformers can address them
  // by stable name.
  const seen = {};
  const headers = headerRow.map((raw) => {
    const norm = normalizeHeader(raw);
    if (!norm) return '';
    seen[norm] = (seen[norm] || 0) + 1;
    return seen[norm] === 1 ? norm : `${norm}_${seen[norm]}`;
  });
  const headerMap = {};
  headers.forEach((h, idx) => {
    if (h) headerMap[h] = idx;
  });
  // Build data row objects keyed by header
  const rawDataRows = rows.slice(headerRowIndex + 1);
  const dataRowsAll = rawDataRows.map((row) => {
    const obj = {};
    headers.forEach((h, idx) => {
      if (!h) return;
      const v = row[idx];
      obj[h] = v === undefined ? null : v;
    });
    return obj;
  });
  const { dataRows, totalsRow } = detectTotalsRow(dataRowsAll, headerMap);
  return {
    name,
    rows,
    headerRowIndex,
    headers,
    headerMap,
    dataRows,
    totalsRow,
    meta: {
      totalRows: rows.length,
      dataRowCount: dataRows.length,
    },
  };
}

async function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

async function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

// Parse CSV string into 2D array (handles quoted fields, embedded commas)
function parseCsvString(text) {
  const rows = [];
  let cur = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      cur.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i += 1;
      cur.push(field);
      rows.push(cur);
      cur = [];
      field = '';
    } else {
      field += c;
    }
  }
  if (field !== '' || cur.length > 0) {
    cur.push(field);
    rows.push(cur);
  }
  return rows;
}

export async function parseCensusFile(file) {
  if (!file) throw new Error('No file provided');
  const lower = (file.name || '').toLowerCase();
  if (lower.endsWith('.csv') || file.type === 'text/csv') {
    const text = await readFileAsText(file);
    const rows = parseCsvString(text);
    return {
      sheets: [buildSheetView('Sheet1', rows)],
      fileName: file.name,
      fileType: 'csv',
      rawText: text,
    };
  }
  // XLSX / XLS
  const buf = await readFileAsArrayBuffer(file);
  const wb = XLSX.read(buf, { type: 'array', cellDates: false });
  const sheets = wb.SheetNames.map((name) => {
    const sheet = wb.Sheets[name];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null, blankrows: false, raw: true });
    return buildSheetView(name, rows);
  });
  return {
    sheets,
    fileName: file.name,
    fileType: lower.endsWith('.xls') ? 'xls' : 'xlsx',
    rawText: null,
  };
}

// Pick the best sheet for analysis when a workbook has multiple. Heuristic:
// the sheet with the most data rows that look census-like.
export function pickPrimarySheet(parsed) {
  if (!parsed?.sheets?.length) return null;
  if (parsed.sheets.length === 1) return parsed.sheets[0];
  // Score each sheet by data-row count + presence of employee identifiers
  const scored = parsed.sheets.map((s) => {
    const headerStr = s.headers.join(' ').toLowerCase();
    const idHit = /employee|first|last|name|salary|wages|reg|gross/.test(headerStr) ? 50 : 0;
    return { sheet: s, score: s.meta.dataRowCount + idHit };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored[0].sheet;
}

// Helper for transformers to coerce numeric values out of Excel/CSV strings.
export function num(v) {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return v;
  const s = String(v).replace(/[$,]/g, '').trim();
  const n = Number(s);
  return isNaN(n) ? 0 : n;
}

export function strOrNull(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s.length === 0 ? null : s;
}
